import { useState } from 'react';
import { Box, Button, Icon, Popover, Tag } from '@nimbus-ds/components';
import { EllipsisIcon } from '@nimbus-ds/icons';
import { Menu, MenuButton } from '@nimbus-ds/patterns';
import { useTranslation } from 'react-i18next';
import { ActionProp, ApplicationsDesktop } from 'commons/components';
import { useIsOrdersAutoCancelEnabled } from 'domains/Orders/Orders/hooks/useIsOrdersAutoCancelEnabled';
import useTranslationOrders from 'domains/Orders/useTranslationOrders';
import OrdersAutoCancelPopover from '../OrdersAutoCancelPopover/OrdersAutoCancelPopover';

interface ExtraOptionsHeaderDesktopProps {
  actions: ActionProp[];
  applications: ActionProp[];
}

function ExtraOptionsHeaderDesktop({
  actions,
  applications,
}: Readonly<ExtraOptionsHeaderDesktopProps>) {
  const { t: tComm } = useTranslation('common');
  const t = useTranslationOrders();
  const isOrdersAutoCancelEnabled = useIsOrdersAutoCancelEnabled();

  const [isVisiblePopoverOptions, setIsVisiblePopoverOptions] = useState(false);
  const [showAutoCancelPopover, setShowAutoCancelPopover] = useState(
    isOrdersAutoCancelEnabled,
  );

  const handleOnOptionClick = (action: ActionProp) => {
    action.onClick?.();
    setIsVisiblePopoverOptions(false);
  };

  if (applications.length === 0) return null;

  if (actions.length === 0)
    return <ApplicationsDesktop applications={applications} />;

  const applicationPopover = (
    <Popover
      key="ordersListMenu"
      position="bottom"
      onVisibility={(visible) => {
        setIsVisiblePopoverOptions(visible);
        setShowAutoCancelPopover(false);
      }}
      visible={isVisiblePopoverOptions}
      content={
        <Box flexDirection="column">
          <Box padding="1" flexDirection="column">
            {actions.map((action) => (
              <MenuButton
                key={`action-${action.children}`}
                label={action.children ?? 'undefined'}
                startIcon={action.icon}
                onClick={() => handleOnOptionClick(action)}
              >
                {isOrdersAutoCancelEnabled && action.name === 'autocancel' && (
                  <Tag appearance="primary">{t('autoCancel.new')}</Tag>
                )}
              </MenuButton>
            ))}
          </Box>
          {applications && applications.length > 0 && (
            <Menu.Section title={tComm('myApps')}>
              <Box flexDirection="column" padding="1">
                {applications.map((application) => (
                  <MenuButton
                    key={`application-${application.children}`}
                    label={application.children ?? 'undefined'}
                    startIcon={application.icon}
                    onClick={() => handleOnOptionClick(application)}
                  />
                ))}
              </Box>
            </Menu.Section>
          )}
        </Box>
      }
    >
      <Button>
        <Icon color="currentColor" source={<EllipsisIcon />} />
        {tComm('moreOptions')}
      </Button>
    </Popover>
  );

  return isOrdersAutoCancelEnabled ? (
    <OrdersAutoCancelPopover showPopover={showAutoCancelPopover}>
      {applicationPopover}
    </OrdersAutoCancelPopover>
  ) : (
    applicationPopover
  );
}

export default ExtraOptionsHeaderDesktop;
